import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Coins, CheckCircle2, AlertCircle, User } from 'lucide-react';

/**
 * DuesTab
 * Lists players selected for the squad (starters, subs, unused subs) and
 * provides a paid/unpaid toggle. Shows running totals of paid vs short.
 */
export const DuesTab = ({ playerStats = [], players = [], playerDues = 0, isViewOnly = false, onStatChange }) => {
  const selected = useMemo(() => {
    return playerStats
      .map((s) => ({ ...s, player: players.find((p) => p.id === s.player_id) }))
      .filter((s) => !!s.player)
      .sort((a, b) => {
        // Starters first, then subs/unused subs, then by name
        if (a.is_starter !== b.is_starter) return a.is_starter ? -1 : 1;
        return (a.player?.name || '').localeCompare(b.player?.name || '');
      });
  }, [playerStats, players]);

  const fee = Number(playerDues) || 0;
  const paidCount = selected.filter((s) => s.dues_paid).length;
  const unpaidCount = selected.length - paidCount;
  const paidTotal = paidCount * fee;
  const shortTotal = unpaidCount * fee;
  const fullTotal = selected.length * fee;

  const formatMoney = (v) => `£${v.toFixed(2)}`;

  return (
    <div className="space-y-4" data-testid="dues-tab">
      {/* Totals header */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dues-totals">
        <TotalCard label="Squad" value={selected.length} icon={User} color="text-gray-700" subtext={fee ? `£${fee.toFixed(2)} per player` : 'Match fee not set'} />
        <TotalCard label="Paid" value={paidCount} icon={CheckCircle2} color="text-emerald-600" subtext={formatMoney(paidTotal)} testId="dues-paid-total" />
        <TotalCard label="Short" value={unpaidCount} icon={AlertCircle} color="text-red-600" subtext={formatMoney(shortTotal)} testId="dues-short-total" />
        <TotalCard label="Expected" value={formatMoney(fullTotal)} icon={Coins} color="text-amber-600" subtext={`${selected.length} × £${fee.toFixed(2)}`} raw />
      </div>

      {/* Players list */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-xl font-semibold font-title">Match Dues</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                {selected.length === 0
                  ? 'Select a squad on the Squad tab to track match dues here.'
                  : 'Toggle each player as Paid / Unpaid. Totals update live.'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {selected.length === 0 ? null : (
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200" data-testid="dues-list">
              {selected.map((s, index) => (
                <div
                  key={s.player_id}
                  className={`flex items-center gap-3 px-3 py-3 ${index < selected.length - 1 ? 'border-b border-gray-100' : ''}`}
                  data-testid={`dues-row-${s.player_id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-xs text-gray-500 font-semibold">
                    {s.is_starter ? 'ST' : 'SB'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{s.player?.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.player?.position || 'No position'}
                      {s.player?.jersey_number ? ` · #${s.player.jersey_number}` : ''}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full shrink-0 font-medium ${s.dues_paid ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {s.dues_paid ? 'Paid' : 'Unpaid'}
                  </span>
                  {isViewOnly ? null : (
                    <Switch
                      checked={!!s.dues_paid}
                      onCheckedChange={(checked) => onStatChange?.(s.player_id, 'dues_paid', checked)}
                      data-testid={`dues-toggle-${s.player_id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function TotalCard({ label, value, icon: Icon, color, subtext, raw, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{value}</p>
          <p className="text-xs text-gray-500 truncate">{label}{subtext ? ` · ${subtext}` : ''}</p>
          {raw ? null : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default DuesTab;
